import { SegmentedBinarySwitch } from '../../../components/SegmentedBinarySwitch/SegmentedBinarySwitch';
import {
    DEFAULT_COLORIZE_BY_ANNOTATOR,
    DEFAULT_REVIEW_FILL_OPACITY,
} from './reviewDisplayConstants';
import styles from './ReviewDisplayControls.module.css';

/**
 * Top-bar controls for admin review overlay: annotator coloring vs original styles, and fill opacity.
 *
 * @param {Object} props
 * @param {boolean} props.colorizeByAnnotator
 * @param {(next: boolean) => void} props.onColorizeByAnnotatorChange
 * @param {number} props.fillOpacity - 0..1
 * @param {(next: number) => void} props.onFillOpacityChange
 */
export default function ReviewDisplayControls({
    colorizeByAnnotator = DEFAULT_COLORIZE_BY_ANNOTATOR,
    onColorizeByAnnotatorChange,
    fillOpacity = DEFAULT_REVIEW_FILL_OPACITY,
    onFillOpacityChange,
}) {
    const pct = Math.round(clampOpacity(fillOpacity) * 100);

    return (
        <div className={styles.root} role="group" aria-label="Review overlay display">
            <span className={styles.label} id="review-display-mode-label">
                Overlay:
            </span>
            <div className={styles.switchWrap}>
                <SegmentedBinarySwitch
                    leftLabel="By annotator"
                    rightLabel="Original style"
                    selectedLeft={colorizeByAnnotator}
                    tone="default"
                    onSelectLeft={() => onColorizeByAnnotatorChange?.(true)}
                    onSelectRight={() => onColorizeByAnnotatorChange?.(false)}
                    ariaLabelledBy="review-display-mode-label"
                    className={styles.reviewDisplaySwitch}
                />
            </div>
            <div className={styles.opacityRow}>
                <label className={styles.opacityLabel} htmlFor="review-fill-opacity-range">
                    Fill opacity
                </label>
                <input
                    id="review-fill-opacity-range"
                    type="range"
                    className={styles.opacityRange}
                    min={0}
                    max={100}
                    step={1}
                    value={pct}
                    onChange={(e) => {
                        const v = Number(e.target.value);
                        if (!Number.isFinite(v)) return;
                        onFillOpacityChange?.(clampOpacity(v / 100));
                    }}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={pct}
                    aria-valuetext={`${pct}%`}
                />
                <span className={styles.opacityValue} aria-hidden="true">
                    {pct}
                    %
                </span>
            </div>
        </div>
    );
}

function clampOpacity(x) {
    if (!Number.isFinite(x)) return DEFAULT_REVIEW_FILL_OPACITY;
    return Math.min(1, Math.max(0, x));
}
